import { useCallback, useMemo } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { useSendTransaction } from '@privy-io/react-auth/solana';
import { 
  Connection, 
  Transaction,
  LAMPORTS_PER_SOL,

} from '@solana/web3.js';
import { solanaUtils } from '../utils/solanaUtils';
import { parsePositionEntryTransaction, parsePositionExitTransaction, parseCrownClaimedTransaction, waitForTransactionConfirmation } from '../utils/transactionParser';

export interface PredictionMarketResult {
  success: boolean;
  signature?: string;
  error?: string;
  cycleId?: string; // Changed from entryNonce to cycleId
  direction?: 'long' | 'short';
  betAmount?: number; // In lamports
  betAmountSol?: number; // In SOL for display
  entryPrice?: number;
  exitPrice?: number; // For close position
  profit?: number; // For close position (in lamports)
  becameKing?: boolean; // For close position
  processed?: boolean; // New: indicates if backend processed the transaction
}

// Updated type definitions for new API responses
interface BuildEnterPositionResponse {
  success: boolean;
  data: {
    transaction: string; // base64 encoded transaction
    roundId: string;
    direction: string;
    betAmount: number;
    entryPrice: number;
    humanEntryPrice: number;
    tokenAddress: string;
    tokenSymbol: string;
    nonce: string;
  };
  message: string;
  error?: string;
}

interface BuildClosePositionResponse {
  success: boolean;
  data: {
    transaction: string; // base64 encoded transaction
    roundId: string;
    exitPrice: number;
    humanExitPrice: number;
    tokenAddress: string;
    tokenSymbol: string;
    nonce: string;
  };
  message: string;
  error?: string;
}

function getBackendUrl(): string {
  return process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
}

export const useTransactions = (solanaWallet: any) => {
  // Initialize Privy hooks
  const { authenticated } = usePrivy();
  const { sendTransaction } = useSendTransaction();
  
  // Check if we're using an embedded wallet or external wallet
  const isEmbeddedWallet = solanaWallet?.walletClientType === 'privy';
  const isExternalWallet = solanaWallet?.walletClientType === 'phantom' || 
                           solanaWallet?.connectorType === 'solana_adapter';
  const connection = useMemo(() => new Connection(solanaUtils.getRpcEndpoint(), 'confirmed'), []);

  // Removed predictionService as it's no longer used with new API flow

  // Note: Twitter username is no longer needed in API calls
  // Backend will lookup username from user profile based on wallet address

  // Helper function to get basic headers (no auth required)
  const getHeaders = useCallback((): Record<string, string> => {
    return {
      'Content-Type': 'application/json'
    };
  }, []);

  /**
   * Build enter position transaction using new simplified API
   */
  const buildTransaction = useCallback(async (
    direction: 'long' | 'short',
    betAmountSol: number = 0.015
  ): Promise<{ transaction: Transaction; nonce: string; betAmount: number; entryPrice: number }> => {
    const response = await fetch(`${getBackendUrl()}/api/round/build-enter-position`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        userWallet: solanaWallet?.address,
        direction,
        // Pass SOL amount; backend converts to lamports
        betAmount: betAmountSol
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const result: BuildEnterPositionResponse = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to build transaction');
    }


    // Deserialize the base64 transaction
    const transaction = Transaction.from(
      Buffer.from(result.data.transaction, 'base64')
    );

    return {
      transaction,
      nonce: result.data.nonce,
      betAmount: result.data.betAmount,
      entryPrice: result.data.entryPrice
    };
  }, [solanaWallet?.address, getHeaders]);

  /**
   * Build close position transaction using new simplified API
   */
  const buildCloseTransaction = useCallback(async (): Promise<{ transaction: Transaction; nonce: string; exitPrice: number }> => {
    const response = await fetch(`${getBackendUrl()}/api/round/build-close-position`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        userWallet: solanaWallet?.address
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const result: BuildClosePositionResponse = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to build close transaction');
    }


    // Deserialize the base64 transaction
    const transaction = Transaction.from(
      Buffer.from(result.data.transaction, 'base64')
    );

    return {
      transaction,
      nonce: result.data.nonce,
      exitPrice: result.data.exitPrice
    };
  }, [solanaWallet?.address, getHeaders]);

  /**
   * Enter a long position in the current round
   */
  const enterLongPosition = useCallback(async (betAmountSol?: number): Promise<PredictionMarketResult> => {
    if (!solanaWallet?.address) {
      return { success: false, error: 'Wallet not connected' };
    }



      try {
        // Step 1: Build transaction via round API
      const buildResponse = await fetch(`${getBackendUrl()}/api/round/build-enter-position`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          userWallet: solanaWallet.address,
          direction: 'long',
          betAmount: betAmountSol || 0.015 // Pass SOL amount, backend will convert to lamports
        })
      });

      if (!buildResponse.ok) {
        const error = await buildResponse.json();
        throw new Error(error.error || 'Failed to build transaction');
      }

      const buildResult = await buildResponse.json();

      
      // Step 2: Send transaction
      const transaction = Transaction.from(
        Buffer.from(buildResult.data.transaction, 'base64')
      );


      let signature: string;
      
      if (isEmbeddedWallet) {
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { 
            showWalletUIs: true,  // Show Privy's signing UI for embedded wallets
          },
          transactionOptions: {
            skipPreflight: false,
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      } else if (isExternalWallet && solanaWallet?.sendTransaction) {
        const result = await solanaWallet.sendTransaction(transaction, connection);
        signature = result.signature || result;
      } else {
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { showWalletUIs: false },
          transactionOptions: {
            skipPreflight: false,
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      }



      // Step 3: Wait for confirmation

      const confirmed = await waitForTransactionConfirmation(connection, signature);
      if (!confirmed) {
        throw new Error('Transaction confirmation failed');
      }


      // Step 4: Parse transaction to get actual entry nonce (critical!)

      const parsedData = await parsePositionEntryTransaction(connection, signature);


      // Step 5: Submit to backend with parsed data

      const submitResponse = await fetch(`${getBackendUrl()}/api/round/enter-position`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          signature,
          walletAddress: solanaWallet.address,
          direction: parsedData.direction,
          entryPrice: parsedData.entryPrice,
          betAmount: parsedData.betAmount, // Store lamports in DB
          cycleId: parsedData.cycleId,
          assetAddress: parsedData.assetAddress,

        })
      });

      if (!submitResponse.ok) {
        console.warn('⚠️ Backend submission failed but transaction is on-chain');
      }


      return {
        success: true,
        signature,
        direction: 'long',
        cycleId: parsedData.cycleId,
        betAmount: parsedData.betAmount,
        betAmountSol: parsedData.betAmount / 1e9, // Convert to SOL for display
        entryPrice: parsedData.entryPrice,
        processed: true
      };

    } catch (error: any) {
      console.error('❌ Long position failed:', error);
      return {
        success: false,
        error: error.message || 'Failed to enter long position',
        processed: false
      };
    }
  }, [isEmbeddedWallet, isExternalWallet, solanaWallet, sendTransaction, connection, getHeaders]);

  /**
   * Enter a short position in the current round
   */
  const enterShortPosition = useCallback(async (betAmountSol?: number): Promise<PredictionMarketResult> => {
    if (!solanaWallet?.address) {
      return { success: false, error: 'Wallet not connected' };
    }



    try {

      // Step 1: Build transaction via round API

      const buildResponse = await fetch(`${getBackendUrl()}/api/round/build-enter-position`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          userWallet: solanaWallet.address,
          direction: 'short',
          betAmount: betAmountSol || 0.015 // Pass SOL amount, backend will convert to lamports
        })
      });

      if (!buildResponse.ok) {
        const error = await buildResponse.json();
        throw new Error(error.error || 'Failed to build transaction');
      }

      const buildResult = await buildResponse.json();

      
      // Step 2: Send transaction

      const transaction = Transaction.from(
        Buffer.from(buildResult.data.transaction, 'base64')
      );

      let signature: string;
      
      if (isEmbeddedWallet) {
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { 
            showWalletUIs: true,  // Show Privy's signing UI for embedded wallets
          },
          transactionOptions: {
            skipPreflight: true,
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      } else if (isExternalWallet && solanaWallet?.sendTransaction) {
        const result = await solanaWallet.sendTransaction(transaction, connection);
        signature = result.signature || result;
      } else {
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { showWalletUIs: false },
          transactionOptions: {
            skipPreflight: true,
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      }



      // Step 3: Wait for confirmation

      const confirmed = await waitForTransactionConfirmation(connection, signature);
      if (!confirmed) {
        throw new Error('Transaction confirmation failed');
      }


      // Step 4: Parse transaction to get actual entry nonce

      const parsedData = await parsePositionEntryTransaction(connection, signature);


      // Step 5: Submit to backend with parsed data

      const submitResponse = await fetch(`${getBackendUrl()}/api/round/enter-position`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          signature,
          walletAddress: solanaWallet.address,
          direction: parsedData.direction,
          entryPrice: parsedData.entryPrice,
          betAmount: parsedData.betAmount, // Store lamports in DB
          cycleId: parsedData.cycleId,
          assetAddress: parsedData.assetAddress,

        })
      });

      if (!submitResponse.ok) {
        console.warn('⚠️ Backend submission failed but transaction is on-chain');
      }




      return {
        success: true,
        signature,
        direction: 'short',
        cycleId: parsedData.cycleId,
        betAmount: parsedData.betAmount,
        betAmountSol: parsedData.betAmount / 1e9, // Convert to SOL for display
        entryPrice: parsedData.entryPrice,
        processed: true
      };

    } catch (error: any) {
      console.error('❌ Short position failed:', error);
      return {
        success: false,
        error: error.message || 'Failed to enter short position',
        processed: false
      };
    }
  }, [isEmbeddedWallet, isExternalWallet, solanaWallet, sendTransaction, connection, getHeaders]);

  /**
   * Close an open position
   */
  const closePosition = useCallback(async (): Promise<PredictionMarketResult> => {
    if (!solanaWallet?.address) {
      return { success: false, error: 'Wallet not connected' };
    }



    try{

      // Step 1: Build transaction via round API

      const buildResponse = await fetch(`${getBackendUrl()}/api/round/build-close-position`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          userWallet: solanaWallet.address
        })
      });

      if (!buildResponse.ok) {
        const error = await buildResponse.json();
        throw new Error(error.error || 'Failed to build close transaction');
      }

      const buildResult = await buildResponse.json();

      
      // Step 2: Send transaction

      const transaction = Transaction.from(
        Buffer.from(buildResult.data.transaction, 'base64')
      );

      let signature: string;
      
      if (isEmbeddedWallet) {
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { 
            showWalletUIs: true,  // Show Privy's signing UI for embedded wallets
          },
          transactionOptions: {
            skipPreflight: true,
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      } else if (isExternalWallet && solanaWallet?.sendTransaction) {
        const result = await solanaWallet.sendTransaction(transaction, connection);
        signature = result.signature || result;
      } else {
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { showWalletUIs: false },
          transactionOptions: {
            skipPreflight: true,
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      }


      console.log(`✅ Close transaction sent: ${signature.slice(0, 8)}...`);

      // Step 3: Wait for confirmation

      const confirmed = await waitForTransactionConfirmation(connection, signature);
      if (!confirmed) {
        throw new Error('Transaction confirmation failed');
      }


      // Step 4: Parse transaction to get actual exit data

      const parsedData = await parsePositionExitTransaction(connection, signature);


      // Step 5: Submit to backend with parsed data

      const submitResponse = await fetch(`${getBackendUrl()}/api/round/close-position`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          signature,
          walletAddress: solanaWallet.address,
          exitPrice: parsedData.exitPrice,
          profit: parsedData.profit,
          cycleId: parsedData.cycleId,
          becameKing: parsedData.becameKing,
          assetAddress: parsedData.assetAddress
        })
      });

      if (!submitResponse.ok) {
        console.warn('⚠️ Backend submission failed but transaction is on-chain');
      }




      return {
        success: true,
        signature,
        cycleId: parsedData.cycleId,
        exitPrice: parsedData.exitPrice,
        profit: parsedData.profit,
        becameKing: parsedData.becameKing,
        processed: true
      };

    } catch (error: any) {
      console.error('❌ Close position failed:', error);
      return {
        success: false,
        error: error.message || 'Failed to close position',
        processed: false
      };
    }
  }, [isEmbeddedWallet, isExternalWallet, solanaWallet, sendTransaction, connection, getHeaders]);

  /**
   * Start a new cycle (claim pot). Does not open a position.
   */
  const startNewCycle = useCallback(async (): Promise<PredictionMarketResult> => {
    if (!solanaWallet?.address) {
      return { success: false, error: 'Wallet not connected' };
    }

    try {

      // Step 1: Build transaction via round API
      const buildResponse = await fetch(`${getBackendUrl()}/api/round/build-start-cycle`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ userWallet: solanaWallet.address })
      });

      if (!buildResponse.ok) {
        const error = await buildResponse.json();
        throw new Error(error.error || 'Failed to build start cycle transaction');
      }

      const buildResult = await buildResponse.json();

      // Step 2: Send transaction
      const transaction = Transaction.from(
        Buffer.from(buildResult.data.transaction, 'base64')
      );

      let signature: string;
      
      if (isEmbeddedWallet) {
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { 
            showWalletUIs: true,  // Show Privy's signing UI for embedded wallets
          },
          transactionOptions: {
            skipPreflight: true,
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      } else if (isExternalWallet && solanaWallet?.sendTransaction) {
        const result = await solanaWallet.sendTransaction(transaction, connection);
        signature = result.signature || result;
      } else {
        const result = await sendTransaction({
          transaction,
          connection,
          uiOptions: { showWalletUIs: false },
          transactionOptions: {
            skipPreflight: true,
            preflightCommitment: 'confirmed'
          }
        });
        signature = result.signature;
      }


      // Step 3: Wait for confirmation
      const confirmed = await waitForTransactionConfirmation(connection, signature);
      if (!confirmed) {
        throw new Error('Transaction confirmation failed');
      }

      // Step 4: Parse transaction to get CrownClaimed event data
      const parsedData = await parseCrownClaimedTransaction(connection, signature);


      // Step 5: Submit to backend with parsed data
      const submitResponse = await fetch(`${getBackendUrl()}/api/round/cycle-completed`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          signature,
          cycleId: parsedData.cycleId,
          winner: parsedData.winner,
          winnerAmount: parsedData.winnerAmount,
          platformFee: parsedData.platformFee,
          endCycleAsset: parsedData.endCycleAsset,
          claimer: solanaWallet.address,

        })
      });

      if (!submitResponse.ok) {
        console.warn('⚠️ Backend submission failed but transaction is on-chain');
      }

      return { 
        success: true, 
        signature, 
        cycleId: parsedData.cycleId,
        processed: true 
      };
    } catch (error: any) {
      console.error('❌ Start new cycle failed:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to start new cycle', 
        processed: false 
      };
    }
  }, [isEmbeddedWallet, isExternalWallet, solanaWallet, sendTransaction, connection, getHeaders]);

  return {
    enterLongPosition,
    enterShortPosition,
    closePosition,
    startNewCycle,
    buildTransaction,
    buildCloseTransaction,
    
    // Legacy functions for backward compatibility (simplified/stub versions)
    enterPosition: useCallback(async (
      roundId: string, 
      direction: 'long' | 'short', 
      tokenAddress: string
    ): Promise<PredictionMarketResult> => {
      // Ignore the roundId and tokenAddress since backend determines current round
      if (direction === 'long') {
        return enterLongPosition();
      } else {
        return enterShortPosition();
      }
    }, [enterLongPosition, enterShortPosition]),
    
    // SOL withdrawal function
    withdrawSol: useCallback(async (toAddress: string, amountSol: number): Promise<PredictionMarketResult> => {
      if (!solanaWallet?.address) {
        return { success: false, error: 'Wallet not connected' };
      }

      try {

        // Create withdrawal transaction
        const { SystemProgram, PublicKey } = await import('@solana/web3.js');
        const transaction = new Transaction().add(
          SystemProgram.transfer({
            fromPubkey: new PublicKey(solanaWallet.address),
            toPubkey: new PublicKey(toAddress),
            lamports: Math.floor(amountSol * LAMPORTS_PER_SOL)
          })
        );

        // Set recent blockhash and fee payer
        const { blockhash } = await connection.getLatestBlockhash();
        transaction.recentBlockhash = blockhash;
        transaction.feePayer = new PublicKey(solanaWallet.address);

        // Simulate transaction before signing (Phantom best practice)
        try {
          transaction.signatures = transaction.signatures.map(sig => ({
            ...sig,
            signature: null
          }));
          
          const simulation = await connection.simulateTransaction(transaction);
          
          if (simulation.value.err) {
            console.error('Transaction simulation failed:', simulation.value.err, simulation.value.logs);
            throw new Error(`Transaction will fail: ${JSON.stringify(simulation.value.err)}`);
          }
        } catch (simError: any) {
          throw new Error(`Pre-flight simulation failed: ${simError.message}`);
        }

        // Sign and send transaction
        let signature: string;

        // For external wallets (Phantom), use separate sign/send pattern
        if (isExternalWallet && solanaWallet?.signTransaction) {
          try {
            const signedTx = await solanaWallet.signTransaction(transaction);
            signature = await connection.sendRawTransaction(signedTx.serialize(), {
              skipPreflight: true,  // Already simulated above
              preflightCommitment: 'confirmed',
              maxRetries: 3
            });
          } catch (walletError: any) {
            console.error('Wallet signing failed:', walletError);
            throw new Error(`Wallet signing failed: ${walletError.message || 'Unknown error'}`);
          }
        } else if (isEmbeddedWallet) {
          const result = await sendTransaction({
            transaction,
            connection,
            uiOptions: { showWalletUIs: true },
            transactionOptions: {
              skipPreflight: true,  // Already simulated above
              preflightCommitment: 'confirmed'
            }
          });
          signature = result.signature;
        } else {
          const result = await sendTransaction({
            transaction,
            connection,
            uiOptions: { showWalletUIs: false },
            transactionOptions: {
              skipPreflight: true,  // Already simulated above
              preflightCommitment: 'confirmed'
            }
          });
          signature = result.signature;
        }

        // Wait for confirmation
        const confirmed = await waitForTransactionConfirmation(connection, signature);
        if (!confirmed) {
          throw new Error('Transaction failed to confirm');
        }


        return {
          success: true,
          signature,
          processed: true
        };

      } catch (error: any) {
        console.error('❌ SOL withdrawal failed:', error);
        return {
          success: false,
          error: error.message || 'Failed to withdraw SOL',
          processed: false
        };
      }
    }, [solanaWallet, sendTransaction, connection, isEmbeddedWallet, isExternalWallet]),
    
    sellTokenCleanup: useCallback(async () => {
      console.warn('sellTokenCleanup is deprecated in prediction market mode');
      return { success: false, error: 'Not supported in prediction market mode' };
    }, []),

    // ✅ NEW: Simple SOL transfer for betting
    transferSolForBet: useCallback(async (betAmountSol: number, bettingPoolWallet: string): Promise<PredictionMarketResult> => {
      if (!authenticated || !solanaWallet?.address) {
        return { success: false, error: 'Wallet not connected' };
      }

      if (betAmountSol <= 0 || betAmountSol > 1) {
        return { success: false, error: 'Invalid bet amount. Must be between 0 and 1 SOL' };
      }

      try {

        // Create simple SOL transfer transaction
        const { SystemProgram, PublicKey } = await import('@solana/web3.js');
        
        const transaction = new Transaction().add(
          SystemProgram.transfer({
            fromPubkey: new PublicKey(solanaWallet.address),
            toPubkey: new PublicKey(bettingPoolWallet),
            lamports: Math.floor(betAmountSol * LAMPORTS_PER_SOL)
          })
        );

        // Set recent blockhash and fee payer
        const { blockhash } = await connection.getLatestBlockhash();
        transaction.recentBlockhash = blockhash;
        transaction.feePayer = new PublicKey(solanaWallet.address);

        // Simulate transaction before signing (Phantom best practice)
        try {
          transaction.signatures = transaction.signatures.map(sig => ({
            ...sig,
            signature: null
          }));
          
          const simulation = await connection.simulateTransaction(transaction);
          
          if (simulation.value.err) {
            console.error('Transaction simulation failed:', simulation.value.err, simulation.value.logs);
            throw new Error(`Transaction will fail: ${JSON.stringify(simulation.value.err)}`);
          }
        } catch (simError: any) {
          throw new Error(`Pre-flight simulation failed: ${simError.message}`);
        }

        // Sign and send transaction
        let signature: string;

        // For external wallets (Phantom), use separate sign/send pattern
        if (isExternalWallet && solanaWallet?.signTransaction) {
          try {
            const signedTx = await solanaWallet.signTransaction(transaction);
            signature = await connection.sendRawTransaction(signedTx.serialize(), {
              skipPreflight: true,  // Already simulated above
              preflightCommitment: 'confirmed',
              maxRetries: 3
            });
          } catch (walletError: any) {
            console.error('Wallet signing failed:', walletError);
            throw new Error(`Wallet signing failed: ${walletError.message || 'Unknown error'}`);
          }
        } else if (isEmbeddedWallet) {
          const result = await sendTransaction({
            transaction,
            connection,
            uiOptions: { showWalletUIs: true },
            transactionOptions: {
              skipPreflight: true,  // Already simulated above
              preflightCommitment: 'confirmed'
            }
          });
          signature = result.signature;
        } else {
          const result = await sendTransaction({
            transaction,
            connection,
            uiOptions: { showWalletUIs: false },
            transactionOptions: {
              skipPreflight: true,  // Already simulated above
              preflightCommitment: 'confirmed'
            }
          });
          signature = result.signature;
        }

        // Wait for confirmation
        const confirmed = await waitForTransactionConfirmation(connection, signature);
        if (!confirmed) {
          throw new Error('Transaction confirmation failed');
        }


        return {
          success: true,
          signature,
          betAmountSol,
          processed: false // Backend will process this
        };

      } catch (error: any) {
        console.error('❌ Bet transfer failed:', error);
        return {
          success: false,
          error: error.message || 'Failed to transfer SOL for bet',
          processed: false
        };
      }
    }, [authenticated, solanaWallet, connection, sendTransaction, isEmbeddedWallet, isExternalWallet]),
    
    // Wallet info for backward compatibility
    walletAddress: solanaWallet?.address,
    
    // Simple config for backward compatibility
    config: {
      rpcEndpoint: solanaUtils.getRpcEndpoint(),
      commitment: 'confirmed' as const
    }
  };
}; 